import {ContentKey, ContentKeyPair, FunnelKey, RoleKey, RoleKeyPair} from "./Keys";

type Selected = 'None' | 'Role' | 'FunnelGroup' | 'MainFunnel' | 'Content' | 'ContentEdit';

export class CjmapSelection {

    private readonly selected: Selected;
    private readonly funnel_key: FunnelKey | null;
    private readonly content_key: ContentKey | null;
    private readonly role_key: RoleKey | null;

    constructor(selected: Selected,
                funnel_key: FunnelKey | null,
                content_key: ContentKey | null,
                role_key: RoleKey | null
    ) {
        this.selected = selected;
        this.funnel_key = funnel_key;
        this.content_key = content_key;
        this.role_key = role_key;
    }

    get NotSelected(): boolean {
        return this.selected === 'None';
    }

    get MainFunnelCellIsSelected(): boolean {
        return this.selected === 'MainFunnel';
    }

    get FunnelGroupCellIsSelected(): boolean {
        return this.selected === 'FunnelGroup';
    }

    get ContentCellIsSelected(): boolean {
        return this.selected === 'Content' || this.selected === 'ContentEdit';
    }

    get ContentCellIsNotEditSelected(): boolean {
        return this.selected === 'Content';
    }

    get ContentCellIsEdit(): boolean {
        return this.selected === 'ContentEdit';
    }

    get RoleCellIsSelected(): boolean {
        return this.selected === 'Role';
    }

    get SelectedFunnelKey(): FunnelKey {
        if (this.NotSelected || this.funnel_key === null) {
            throw new Error('not selected');
        }
        return this.funnel_key;
    }

    get SelectedContentKey(): ContentKey {
        if (!this.ContentCellIsSelected || this.content_key === null) {
            throw new Error('not selected');
        }
        return this.content_key;
    }


    get SelectedRoleKey(): RoleKey {
        if (!this.RoleCellIsSelected || this.role_key === null) {
            throw new Error('not selected');
        }
        return this.role_key;
    }

    selectedMainFunnel(funnel_key: FunnelKey) {
        return this.MainFunnelCellIsSelected
            && this.funnel_key === funnel_key;
    }

    selectedFunnelGroup(funnel_key: FunnelKey) {
        return this.FunnelGroupCellIsSelected
            && this.funnel_key === funnel_key;
    }

    selectedContentCell(key_pair: ContentKeyPair) {
        return this.ContentCellIsSelected
            && this.funnel_key === key_pair.FunnelKey
            && this.content_key === key_pair.LineKey;
    }

    selectedAndEditContentCell(key_pair: ContentKeyPair) {
        return this.selected === 'ContentEdit'
            && this.funnel_key === key_pair.FunnelKey
            && this.content_key === key_pair.LineKey;
    }

    selectedRoleCell(key_pair: RoleKeyPair) {
        return this.RoleCellIsSelected
            && this.funnel_key === key_pair.FunnelKey
            && this.role_key === key_pair.LineKey;
    }


    selectMainFunnelCell(funnel_key: FunnelKey) {
        return this.make('MainFunnel', {funnel_key});
    }

    selectFunnelGroupCell(funnel_key: FunnelKey) {
        return this.make('FunnelGroup', {funnel_key});
    }

    selectContentCell(key_pair: ContentKeyPair) {
        return this.make('Content', {funnel_key: key_pair.FunnelKey, content_key: key_pair.LineKey});
    }

    selectRoleCell(key_pair: RoleKeyPair) {
        return this.make('Role', {funnel_key: key_pair.FunnelKey, role_key: key_pair.LineKey});
    }

    editContentCell() {
        return this.make('ContentEdit', {});
    }

    endEditContentCell() {
        return this.make('Content', {});
    }

    clearSelect() {
        return this.make('None', {});
    }

    private make(selected: Selected, selection: {
        funnel_key?: FunnelKey;
        content_key?: ContentKey;
        role_key?: RoleKey;
    }) {
        return new CjmapSelection(
            selected,
            selection.funnel_key ? selection.funnel_key : this.funnel_key,
            selection.content_key ? selection.content_key : this.content_key,
            selection.role_key ? selection.role_key : this.role_key,
        );
    }

    toSelectedPositionString(): string {
        const SEP = ':';
        if (this.NotSelected) {
            return this.selected;
        } else if (this.RoleCellIsSelected) {
            return this.selected + SEP + this.funnel_key + SEP + this.role_key;
        } else if (this.FunnelGroupCellIsSelected) {
            return this.selected + SEP + this.funnel_key;
        } else if (this.MainFunnelCellIsSelected) {
            return this.selected + SEP + this.funnel_key;
        } else if (this.ContentCellIsSelected) {
            return this.selected + SEP + this.funnel_key + SEP + this.content_key;
        }

        throw new Error('unexpected selected state');
    }

    toJSON() {
        return {
            selected: this.selected,
            funnel_key: this.funnel_key,
            content_key: this.content_key,
            role_key: this.role_key,
        };
    }

    static createNew(): CjmapSelection {
        return new CjmapSelection('None', null, null, null);
    }

    static from(original: CjmapSelection) {
        return new CjmapSelection(original.selected, original.funnel_key, original.content_key, original.role_key);
    }

}
